"use client";

import Link from "next/link";
import { ArrowUpRight, Search } from "lucide-react";
import { useMemo, useState } from "react";
import type { ProductPriceSourceKind } from "@/lib/product-price-source";
import { PRICE_MID_LABEL } from "@/lib/price-labels";

export interface ProductMarketOverviewRow {
  marketSlug: string;
  marketName: string;
  cityName: string;
  minPrice: number;
  maxPrice: number;
  avgPrice: number;
  unit: string;
  recordedDate: string;
}

function normalizeSearch(value: string) {
  return value
    .toLocaleLowerCase("tr-TR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function money(value: number) {
  return value.toLocaleString("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function shortDate(value: string) {
  const date = new Date(`${value.slice(0, 10)}T12:00:00Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
}

export default function ProductMarketOverview({ rows, productName, sourceKind }: { rows: ProductMarketOverviewRow[]; productName: string; sourceKind: ProductPriceSourceKind }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(false);

  const uniqueRows = useMemo(() => {
    const seen = new Set<string>();
    return rows.filter((row) => {
      const key = row.marketSlug || `${row.cityName}-${row.marketName}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rows]);

  const filteredRows = useMemo(() => {
    const needle = normalizeSearch(query.trim());
    if (!needle) return uniqueRows;
    return uniqueRows.filter((row) => normalizeSearch(`${row.marketName} ${row.cityName}`).includes(needle));
  }, [query, uniqueRows]);

  const visibleRows = expanded || query ? filteredRows : filteredRows.slice(0, 4);
  const mobileRows = expanded ? filteredRows : filteredRows.slice(0, 3);
  const isBorsa = sourceKind === "borsa";
  const sourceSingular = isBorsa ? "borsa" : "hal";
  const allSourcesLabel = isBorsa ? "borsayı" : "hali";

  if (uniqueRows.length === 0) return null;

  return (
    <section id={`${sourceKind}-fiyatlari`} className="border-b border-(--color-border-soft) pb-8 pt-7 md:pb-12 md:pt-10" aria-labelledby={`${sourceKind}-fiyatlari-baslik`}>
      <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <div className="flex flex-col gap-4 border-b border-(--color-border-soft) pb-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="type-eyebrow">{isBorsa ? "Borsa karşılaştırması" : "Hal karşılaştırması"}</p>
              <h2 id={`${sourceKind}-fiyatlari-baslik`} className="type-h2 mt-2">
                {isBorsa ? "Güncel borsa fiyatları" : "Bugünkü hal fiyatları"}
              </h2>
              <p className="mt-1 text-sm text-(--color-muted)">
                {uniqueRows.length} {sourceSingular} kaynağının doğrulanmış son kaydı
              </p>
            </div>
            <label className="relative hidden w-full max-w-[280px] md:block">
              <span className="sr-only">{isBorsa ? "Borsa veya şehir ara" : "Hal veya şehir ara"}</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-(--color-muted)" aria-hidden />
              <input value={query} onChange={(event) => setQuery(event.target.value)} type="search" placeholder={isBorsa ? "Borsa veya şehir ara" : "Hal veya şehir ara"} className="h-10 w-full border border-(--color-border) bg-white pl-10 pr-3 text-sm outline-none transition focus:border-[#101412]" />
            </label>
          </div>

          <div className="hidden md:block">
            <div className="grid grid-cols-[1.25fr_.7fr_1fr_.65fr_28px] gap-4 border-b border-(--color-border-soft) py-3 font-(family-name:--font-mono) text-[10px] font-bold uppercase tracking-[0.1em] text-(--color-muted)">
              <span>{isBorsa ? "Borsa / şehir" : "Hal / şehir"}</span>
              <span>{PRICE_MID_LABEL}</span>
              <span>Fiyat aralığı</span>
              <span>Kayıt</span>
              <span />
            </div>
            {visibleRows.map((row) => (
              <Link key={`${row.marketSlug}-${row.recordedDate}`} href={`/${sourceKind}/${row.marketSlug}`} className="group grid grid-cols-[1.25fr_.7fr_1fr_.65fr_28px] items-center gap-4 border-b border-(--color-border-soft) py-4 transition-colors hover:bg-[#f7f8f5]">
                <span>
                  <strong className="block text-sm text-[#101412]">{row.marketName}</strong>
                  <span className="mt-1 block text-xs text-(--color-muted)">{row.cityName}</span>
                </span>
                <strong className="font-(family-name:--font-mono) text-sm text-[#101412]">₺{money(row.avgPrice)}</strong>
                <span className="font-(family-name:--font-mono) text-xs text-[#101412]">
                  ₺{money(row.minPrice)} – ₺{money(row.maxPrice)}
                </span>
                <time className="text-xs text-(--color-muted)" dateTime={row.recordedDate}>
                  {shortDate(row.recordedDate)}
                </time>
                <ArrowUpRight className="h-4 w-4 text-(--color-muted) transition group-hover:text-[#101412]" aria-hidden />
              </Link>
            ))}
          </div>

          <div className="md:hidden">
            {mobileRows.map((row) => (
              <Link key={`${row.marketSlug}-${row.recordedDate}`} href={`/${sourceKind}/${row.marketSlug}`} className="grid grid-cols-[minmax(0,1fr)_auto_18px] items-center gap-3 border-b border-(--color-border-soft) py-4">
                <span className="min-w-0">
                  <strong className="block truncate text-sm text-[#101412]">{row.cityName}</strong>
                  <span className="mt-0.5 block truncate text-xs text-(--color-muted)">{row.marketName}</span>
                </span>
                <span className="text-right">
                  <strong className="block font-(family-name:--font-mono) text-sm text-[#101412]">₺{money(row.avgPrice)}</strong>
                  <span className="mt-0.5 block text-[10px] text-(--color-muted)">
                    /{row.unit} · {shortDate(row.recordedDate)}
                  </span>
                </span>
                <ArrowUpRight className="h-4 w-4 text-(--color-muted)" aria-hidden />
              </Link>
            ))}
          </div>

          {filteredRows.length === 0 ? <p className="py-8 text-center text-sm text-(--color-muted)">Aramanızla eşleşen {sourceSingular} bulunamadı.</p> : null}
          {filteredRows.length > 3 || expanded ? (
            <button type="button" onClick={() => setExpanded((value) => !value)} className="mt-5 w-full border border-[#101412] px-4 py-3 text-sm font-bold text-[#101412] transition hover:bg-[#101412] hover:text-white md:w-auto">
              {expanded ? "Daha az göster" : `Tüm ${filteredRows.length} ${allSourcesLabel} göster`}
            </button>
          ) : null}
        </div>

        <aside className="hidden border border-(--color-border-soft) bg-[#f7f8f5] p-6 lg:block" aria-label="Karşılaştırma ipuçları">
          <p className="type-eyebrow">Karşılaştırırken</p>
          <h3 className="type-h3 mt-3">Doğru fiyatı okuyun</h3>
          <ul className="mt-5 space-y-4 text-sm leading-6 text-(--color-muted)">
            <li>
              <strong className="text-[#101412]">Birim:</strong> Kayıtları aynı satış birimi içinde karşılaştırın.
            </li>
            <li>
              <strong className="text-[#101412]">Tarih:</strong> Kaynakların yayın saatleri birbirinden farklı olabilir.
            </li>
            <li>
              <strong className="text-[#101412]">Kalite:</strong> Min–maks aralığı kalite ve ambalaj farkını içerebilir.
            </li>
          </ul>
          <p className="mt-6 border-t border-(--color-border-soft) pt-4 text-xs leading-5 text-(--color-muted)">
            {productName} için gösterilen değerler {isBorsa ? "ticaret borsası kaydıdır" : "toptan hal fiyatıdır"}; perakende raf fiyatı değildir.
          </p>
        </aside>
      </div>
    </section>
  );
}
